<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';

  let messages = [];
  let viewStatus = 'Open'; // Default filter
  let isLoading = true;
  
  // Edit State
  let selectedMsg = null;
  let remarksInput = '';

  onMount(fetchMessages);

  // Re-fetch when filter changes
  $: if (viewStatus) fetchMessages();

  async function fetchMessages() {
    isLoading = true;
    try {
      const res = await authFetch(getApiUrl(`/admin/messages?status=${viewStatus}`));
      messages = await res.json();
    } catch(e) {
      console.error(e);
    } finally {
      isLoading = false;
    }
  }

  function openReview(msg) {
      selectedMsg = msg;
      remarksInput = msg.adminRemarks || '';
  }

  async function updateStatus(newStatus) {
      if (!selectedMsg) return;
      try {
          const res = await authFetch(getApiUrl(`/admin/messages/${selectedMsg._id}`), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: newStatus, adminRemarks: remarksInput })
          });
          
          if(res.ok) {
              selectedMsg = null; // Close modal
              fetchMessages(); // Refresh list
          }
      } catch(e) {
          alert($t('admin.messages.update_failed'));
      }
  }
</script>

{#if selectedMsg}
    <div class="modal-backdrop" on:click={() => selectedMsg = null}>
        <div class="modal-content" on:click|stopPropagation>
            <h3>{$t('admin.messages.review_title')}</h3>
            
            <div class="info-grid">
                <label>{$t('admin.messages.from')}:</label> <span>{selectedMsg.senderName} ({selectedMsg.email})</span>
                {#if selectedMsg.userName}
                    <label>{$t('admin.messages.user_profile')}:</label> <span class="badge">✅ {$t('admin.messages.member')}: {selectedMsg.userName}</span>
                {/if}
                <label>{$t('admin.messages.phone')}:</label> <span>{selectedMsg.phoneCountry} {selectedMsg.phoneNumber}</span>
                <label>{$t('admin.messages.date')}:</label> <span>{new Date(selectedMsg.createdAt).toLocaleString()}</span>
            </div>
            
            <div class="message-box">
                <strong>{$t('admin.messages.message_label')}:</strong>
                <textarea class="message-textarea" readonly value={selectedMsg.message}></textarea>
            </div>

            <div class="admin-section">
                <label>{$t('admin.messages.admin_remarks')}:</label>
                <textarea rows="3" bind:value={remarksInput} placeholder={$t('admin.messages.internal_notes_placeholder')}></textarea>
            </div>

            <div class="actions">
                <button class="secondary" on:click={() => selectedMsg = null}>{$t('admin.messages.cancel')}</button>
                
                {#if selectedMsg.status === 'Open'}
                    <button class="close-btn" on:click={() => updateStatus('Closed')}>{$t('admin.messages.close_ticket')}</button>
                {:else}
                    <button class="reopen-btn" on:click={() => updateStatus('Open')}>{$t('admin.messages.reopen_ticket')}</button>
                {/if}
            </div>
        </div>
    </div>
{/if}


<div class="admin-container">
    <div class="header">
        <h2>{$t('admin.messages.title')}</h2>
        <div class="toggle">
            <button class:active={viewStatus === 'Open'} on:click={() => viewStatus = 'Open'}>{$t('admin.messages.open')}</button>
            <button class:active={viewStatus === 'Closed'} on:click={() => viewStatus = 'Closed'}>{$t('admin.messages.closed')}</button>
        </div>
    </div>

    {#if isLoading}
        <p>{$t('admin.messages.loading')}</p>
    {:else if messages.length === 0}
        <p class="empty">
            {#if viewStatus === 'Open'}
                {$t('admin.messages.no_open_messages')}
            {:else}
                {$t('admin.messages.no_closed_messages')}
            {/if}
        </p>
    {:else}
        <div class="list">
            {#each messages as msg}
                <div class="msg-card" on:click={() => openReview(msg)}>
                    <div class="msg-header">
                        <span class="sender">{msg.senderName}</span>
                        <span class="date">{new Date(msg.createdAt).toLocaleDateString()}</span>
                    </div>
                    <p class="preview">{msg.message.substring(0, 80)}...</p>
                    {#if msg.userName}
                        <div class="member-tag">{$t('admin.messages.member')}</div>
                    {/if}
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .admin-container { max-width: 800px; margin: 2rem auto; padding: 0 1rem; }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
    h2 { margin: 0; }
    
    .toggle { display: flex; background: #eee; border-radius: 4px; padding: 2px; }
    .toggle button { background: none; border: none; padding: 0.5rem 1rem; cursor: pointer; border-radius: 4px; }
    .toggle button.active { background: white; font-weight: bold; shadow: 0 1px 3px rgba(0,0,0,0.1); }

    .list { display: grid; gap: 1rem; }
    .msg-card { background: white; padding: 1rem; border: 1px solid #eee; border-radius: 8px; cursor: pointer; transition: transform 0.1s; }
    .msg-card:hover { border-color: #ccc; transform: translateY(-2px); }
    
    .msg-header { display: flex; justify-content: space-between; margin-bottom: 0.5rem; font-weight: bold; }
    .date { font-weight: normal; color: #888; font-size: 0.9rem; }
    .preview { color: #555; margin: 0; }
    .member-tag { display: inline-block; background: #e3f2fd; color: #0d47a1; font-size: 0.8rem; padding: 2px 6px; border-radius: 4px; margin-top: 0.5rem; }

    /* Modal Styles */
    .modal-backdrop { position: fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.5); display:flex; justify-content:center; align-items:center; z-index:2000; }
    .modal-content { background:white; padding:2rem; border-radius:8px; width:95%; max-width:1200px; }
    
    .info-grid { display: grid; grid-template-columns: auto 1fr; gap: 0.5rem 1rem; margin-bottom: 1.5rem; font-size: 0.95rem; }
    .info-grid label { font-weight: bold; color: #666; }
    .badge { color: #28a745; font-weight: bold; }
    
    .message-box { background: #f9f9f9; padding: 1rem; border-radius: 4px; margin-bottom: 1rem; }
    .message-textarea { width: 100%; height: 350px; margin-top: 0.5rem; padding: 0.75rem; border: 1px solid #ddd; border-radius: 4px; background: #fff; font-family: monospace; font-size: 0.85rem; white-space: pre-wrap; resize: vertical; overflow-y: auto; }
    .admin-section textarea { width: 100%; margin-top: 0.5rem; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; }
    
    .actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1.5rem; }
    button { padding: 0.6rem 1.2rem; border-radius: 4px; border: none; cursor: pointer; }
    .secondary { background: #ccc; }
    .close-btn { background: #28a745; color: white; }
    .reopen-btn { background: #ffc107; }
</style>